import type { ReactNode } from 'react';

/** A stat tile: tiny label, one hero number, a caption, then optional rows. */
export function StatGroup({ label, hero, caption, children }: {
  label: string;
  hero: ReactNode;
  caption?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 px-4 py-4 sm:px-5 sm:py-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">{label}</div>
      <div className="mt-2.5 text-[1.6rem] font-semibold leading-none tracking-tight tabular-nums text-gray-900 dark:text-gray-50">{hero}</div>
      {caption && <div className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">{caption}</div>}
      {children && <div className="mt-3 space-y-1 text-[13px] leading-tight">{children}</div>}
    </div>
  );
}

export function StatRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 min-w-0">
      <span className="text-gray-400 dark:text-gray-500">{label}</span>
      <span className="min-w-0 truncate font-medium tabular-nums text-gray-700 dark:text-gray-200">{value}</span>
    </div>
  );
}

/** Wrapper that renders tiles as a crisp gap-px strip (mobileCols on phone → N col desktop). */
export function StatStrip({ cols = 3, mobileCols = 1, children }: { cols?: 2 | 3 | 4; mobileCols?: 1 | 2; children: ReactNode }) {
  const sm = cols === 2 ? 'sm:grid-cols-2' : cols === 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3';
  const base = mobileCols === 2 ? 'grid-cols-2' : 'grid-cols-1';
  return (
    <div className="card overflow-hidden">
      <div className={`grid ${base} gap-px bg-gray-100 dark:bg-gray-700/70 ${sm}`}>{children}</div>
    </div>
  );
}
